import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import { useEffect, useRef, useState } from "react";
import { has } from "react-widgets/lib/util/_";
import styled from "styled-components";
import { HorizontalLineDivider } from "styledComponents/Activity";
import {
  AssignListContainer,
  AssignListItem,
  AssignListText,
  AssigneeSearchBarContainer,
  AssigneeSearchBarWrapper,
  AssigneeSearchIcon,
  AssigneeSearchInput,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  InputField,
  InputLabel,
  RedStar,
  SearchBarWithAssignListContainer,
} from "styledComponents/common";
import {
  RoundImageCross,
  RoundPersonImage,
} from "styledComponents/createProject";

import avatar5 from "../../assets/img/avatar_5.svg";
import dashboardStatIconWhite from "../../assets/img/icons/dashboard-stat-icon-white.svg";
import dashboardStatIconHover from "../../assets/img/icons/dashboradStatIconHover.svg";
import plusIcon from "../../assets/img/icons/plus-secondary.svg";
import removeAssignee from "../../assets/img/icons/remove_assignee_black.svg";
import searchIcon from "../../assets/img/icons/search.svg";
import tickIcon from "../../assets/img/icons/tick_primary_new_26.svg";
import DropdownLoader from "../DropdownLoader/DropdownLoader";

const MemberSelectionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px;
  border-radius: ${({ theme }) => theme.borderRadii.input};
  background-color: ${({ theme }) => theme.colors.main.white};
  border: 1px solid
    ${({ theme, active }) =>
      active
        ? theme.input.borderColor.focus
        : theme.colors.border.secondaryLight};
  cursor: pointer;
  position: relative;
  &:hover {
    border-color: ${({ theme, active }) =>
      active ? theme.input.borderColor.focus : theme.input.borderColor.hover};
    box-shadow: ${({ theme, active }) =>
      active ? theme.input.focusShadow : theme.input.focusShadow};
  }
`;

const SelectedMemberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0px;
  background-color: ${({ theme }) => theme.colors.main.white};
  position: relative;
  width: 100%;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
`;

const MemberName = styled(CommonText)`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MemberRole = styled(CommonText)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const RemoveButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid #d3e0fc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 30px;
  left: 0;
  z-index: 1000;
  width: 100%;
`;

const TickIconContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 1;
`;

const AvailabilityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 4px 8px;
  width: max-content;
  min-height: 28px;
  background: #fff1f1;
  border-radius: 8px;
  flex: none;
  flex-grow: 0;
  opacity: ${({ hasConflict }) => (hasConflict ? 1 : 0)};
  cursor: ${({ hasConflict }) => (hasConflict ? "pointer" : "default")};
`;

const AvailabilityIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #ffffff;
  border-radius: 4px;
  flex: none;
  flex-grow: 0;
`;

const AvailabilityText = styled(CommonText)`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #ff5722;
  flex: none;
  order: 0;
  flex-grow: 0;
  width: max-content;
`;

const AddMultipleMemberDropDownNew = ({
  isLoading,
  selectedOrganization,
  assigneesList = [],
  membersList = [],
  index,
  handleAssign,
  handleRemove,
  onConflictClick,
  assigneeIsLoading,
  loadingAssigneeId,
  autoPlacement = true,
  isRequired = true,
}) => {
  const [active, setActive] = useState(false);
  const [addList, setAddList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredAvailabilityPersonId, setHoveredAvailabilityPersonId] =
    useState(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Helper function to check if a member is selected
  const isMemberSelected = member => {
    if (!assigneesList || assigneesList.length === 0) {
      return false;
    }
    return assigneesList.some(assignee => {
      // Match by user_id if both exist
      if (assignee.user_id && member.user_id) {
        if (assignee.user_id === member.user_id) {
          return true;
        }
      }
      // Match by id if both exist
      if (assignee.id && member.id) {
        if (assignee.id === member.id) {
          return true;
        }
      }
      return false;
    });
  };

  // Filter members list based on search term only (don't filter out selected members)
  useEffect(() => {
    if (!membersList || membersList.length === 0) {
      setAddList([]);
      return;
    }

    let filteredList = [];

    // Filter by search term if provided
    membersList.forEach(member => {
      const name = member.name || "";

      // If search term exists, filter by name
      if (searchTerm && searchTerm !== "") {
        if (
          name.toLowerCase().indexOf(searchTerm.toLowerCase().trim()) === -1
        ) {
          return; // Skip this member if name doesn't match search
        }
      }

      // Add member to filtered list (including selected ones)
      filteredList.push({
        id: member.id,
        user_id: member.user_id,
        name: member.name,
        avatar: member.avatar,
        phone: member.phone,
        role: member.role || member.designation || "Member",
        designation: member.designation,
        has_conflict: member.has_conflict,
      });
    });

    setAddList(filteredList);
  }, [membersList, searchTerm]);

  const onSearchTermChange = e => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  // Handle click outside to close active state
  useEffect(() => {
    function handleClickOutside(event) {
      const clickedNode = event.target;
      const isInsideDropdown =
        wrapperRef.current && wrapperRef.current.contains(clickedNode);
      const isInsideRightPanel =
        clickedNode.closest &&
        clickedNode.closest('[role="complementary"][aria-label="right-panel"]');

      if (!isInsideDropdown && !isInsideRightPanel) {
        setActive(false);
        setSearchTerm("");
      }
    }

    // Bind the event listener only when active
    if (active) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [active]);

  const handleClick = () => {
    setActive(!active);
    if (!active) {
      setSearchTerm("");
    }
  };

  const handleMemberSelect = person => {
    // Check if member is already selected
    const isSelected = isMemberSelected(person);

    if (isSelected) {
      // If already selected, remove the member
      if (handleRemove) {
        handleRemove(person);
      }
    } else {
      // If not selected, add the member
      if (handleAssign) {
        handleAssign(person);
      }
    }
    setSearchTerm("");
  };

  return (
    <CommonGrid
      ref={wrapperRef}
      style={{ width: "100%", position: "relative" }}
      disableResponsive
      columns="1fr"
      gap="10px"
    >
      <InputLabel>
        Assignee {isRequired ? <RedStar>*</RedStar> : null}
      </InputLabel>
      <MemberSelectionButton active={active} onClick={handleClick}>
        <CommonImage src={plusIcon} alt="add member" />
      </MemberSelectionButton>

      {/* Dropdown List Container */}
      {active && (
        <DropdownContainer ref={dropdownRef}>
          <AssignListContainer width="100%" padding="0">
            <SearchBarWithAssignListContainer>
              <AssigneeSearchBarWrapper>
                <AssigneeSearchBarContainer>
                  <AssigneeSearchIcon src={searchIcon} alt="search" />
                  <AssigneeSearchInput
                    value={searchTerm}
                    onChange={onSearchTermChange}
                    placeholder="Search..."
                    autoFocus
                  />
                </AssigneeSearchBarContainer>
              </AssigneeSearchBarWrapper>

              <DropdownLoader loading={isLoading} />
              {addList && addList.length > 0 ? (
                addList.map((person, i) => {
                  const isSelected = isMemberSelected(person);
                  return assigneeIsLoading &&
                    loadingAssigneeId &&
                    loadingAssigneeId === person.id ? (
                    <AssignListItem columns="100%" key={i}>
                      <DropdownLoader loading />
                    </AssignListItem>
                  ) : (
                    <AssignListItem
                      key={i}
                      onClick={() => handleMemberSelect(person)}
                      columns="40px 150px auto auto"
                      padding="10px 12px"
                    >
                      <CommImageNameComp
                        imgSource={person.avatar ? person.avatar : avatar5}
                        name={person.name || "Unknown"}
                        size="40px"
                        showAddDesignation={false}
                      />

                      <AssignListText>{person.name}</AssignListText>

                      <AvailabilityBadge
                        onMouseEnter={() =>
                          setHoveredAvailabilityPersonId(
                            person.id || person.user_id,
                          )
                        }
                        onMouseLeave={() =>
                          setHoveredAvailabilityPersonId(null)
                        }
                        onClick={event => {
                          event.stopPropagation();
                          if (person.has_conflict && onConflictClick) {
                            onConflictClick(person);
                          }
                        }}
                        hasConflict={person.has_conflict}
                      >
                        <AvailabilityText>
                          Has overlapping{" "}
                          {selectedOrganization.type === "service"
                            ? "jobs"
                            : "tasks"}
                        </AvailabilityText>
                        <AvailabilityIconWrapper>
                          <CommonImage
                            // // hoveredAvailabilityPersonId ===
                            //     (person.id || person.user_id)
                            //       ? dashboardStatIconHover
                            //       :
                            src={dashboardStatIconWhite}
                            alt="availability"
                            size="24px"
                          />
                        </AvailabilityIconWrapper>
                      </AvailabilityBadge>

                      <TickIconContainer>
                        {isSelected && (
                          <CommonImage
                            src={tickIcon}
                            alt="selected"
                            size="22px"
                          />
                        )}
                      </TickIconContainer>
                    </AssignListItem>
                  );
                })
              ) : (
                <CommonText
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: "#999",
                  }}
                >
                  No members available
                </CommonText>
              )}
            </SearchBarWithAssignListContainer>
          </AssignListContainer>
        </DropdownContainer>
      )}
      {/* Selected Members Display Container */}
      {assigneesList && assigneesList.length > 0 && (
        <CommonFlex direction="column" gap="5px">
          {assigneesList.map((member, index) => (
            <>
              <SelectedMemberContainer
                key={member.id || member.user_id || index}
              >
                <CommImageNameComp
                  imgSource={member.avatar ? member.avatar : avatar5}
                  name={member.name || "Unknown"}
                  size="40px"
                  showAddDesignation={false}
                  designation={member.designation}
                />
                <RemoveButton
                  onClick={() => {
                    if (handleRemove) {
                      handleRemove(member);
                    }
                  }}
                  title="Remove member"
                >
                  <CommonImage src={removeAssignee} alt="remove" size="8px" />
                </RemoveButton>
              </SelectedMemberContainer>
              {index !== assigneesList.length - 1 && <HorizontalLineDivider />}
            </>
          ))}
        </CommonFlex>
      )}
    </CommonGrid>
  );
};

export default AddMultipleMemberDropDownNew;
